import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import TernaryDiagram from "../components/TernaryDiagram";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { 
  Plus, Trash2, Save, FlaskConical, 
  AlertTriangle, X, Search, Package, CheckCircle, Droplet, Download, DollarSign,
  Sparkles, RefreshCw, ArrowRight, Link2, HelpCircle
} from "lucide-react";

export default function RecipeBuilderPage() {
  const { recipeId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [recipe, setRecipe] = useState({
    name: "",
    description: "",
    total_grams: 100,
    concentration_percent: 20, // Target % of raw materials
    ingredients: [],
    notes: ""
  });
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showUseDialog, setShowUseDialog] = useState(false);
  const [batchMultiplier, setBatchMultiplier] = useState(1);
  const [usingMaterials, setUsingMaterials] = useState(false);
  
  // AI Resolution states
  const [aiResolutions, setAiResolutions] = useState([]);
  const [resolvingAI, setResolvingAI] = useState(false);
  const [showAIPanel, setShowAIPanel] = useState(false);

  useEffect(() => {
    fetchMaterials();
    if (recipeId) {
      fetchRecipe();
    }
  }, [recipeId, token]);

  // When materials load and recipe has ingredients, enrich them with material data
  useEffect(() => {
    if (materials.length > 0 && recipe.ingredients.length > 0) {
      enrichIngredientsWithMaterials();
    }
  }, [materials]);

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(`${API}/materials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMaterials(response.data);
    } catch (error) {
      console.error("Failed to fetch materials:", error);
    }
  };

  const fetchRecipe = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/recipes/${recipeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const recipeData = response.data;
      setRecipe(recipeData);
    } catch (error) {
      toast.error("Failed to load recipe");
      navigate("/my-recipes");
    } finally {
      setLoading(false);
    }
  };

  // Enrich recipe ingredients with data from inventory materials
  const enrichIngredientsWithMaterials = () => {
    const rawMaterialBudget = (recipe.concentration_percent / 100) * recipe.total_grams;
    
    const enrichedIngredients = recipe.ingredients.map(ing => {
      // Find matching material in inventory (check name, synonyms, and CAS)
      const material = materials.find(m => {
        const materialName = (ing.material_name || '').toLowerCase();
        const materialId = ing.material_id;
        
        // Match by material_id first
        if (materialId && m.material_id === materialId) {
          return true;
        }
        
        // Match by name
        if (m.name.toLowerCase() === materialName) {
          return true;
        }
        
        // Match by synonyms
        if (m.synonyms) {
          const synonyms = m.synonyms.toLowerCase().split(',').map(s => s.trim());
          if (synonyms.includes(materialName)) {
            return true;
          }
        }
        
        return false;
      });
      
      if (material) {
        // Calculate grams if percentage is set but grams is not
        let grams = parseFloat(ing.grams) || 0;
        const pct = parseFloat(ing.percentage) || 0;
        
        if (pct > 0 && grams === 0) {
          const rawGramsNeeded = (pct / 100) * rawMaterialBudget;
          const solutionPct = material.solution_percentage || 100;
          grams = solutionPct > 0 ? rawGramsNeeded / (solutionPct / 100) : rawGramsNeeded;
        }
        
        return {
          ...ing,
          material_id: material.material_id,
          material_name: material.name,
          note_type: material.note_type || ing.note_type || "unclassified", // Updated field name
          solution_percentage: material.solution_percentage || 100,
          solution_type: material.solution_type || "Alcohol",
          price_per_gram: material.price_per_gram || 0,
          ifra_limit: material.ifra_limit, // Add IFRA limit for validation
          grams: grams
        };
      }
      
      return ing;
    });
    
    setRecipe(prev => ({ ...prev, ingredients: enrichedIngredients }));
  };

  const handleSave = async () => {
    if (!recipe.name.trim()) {
      toast.error("Recipe name is required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...recipe,
        batch_price: totalBatchPrice > 0 ? totalBatchPrice : null
      };

      if (recipeId) {
        await axios.put(`${API}/recipes/${recipeId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Recipe updated");
      } else {
        const response = await axios.post(`${API}/recipes`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Recipe created");
        navigate(`/recipe-builder/${response.data.recipe_id}`);
      }
    } catch (error) {
      toast.error("Failed to save recipe");
    } finally {
      setSaving(false);
    }
  };

  const addIngredient = (material) => {
    const exists = recipe.ingredients.find(i => i.material_id === material.material_id);
    if (exists) {
      toast.info("Material already in recipe");
      return;
    }

    setRecipe(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, {
        material_id: material.material_id,
        material_name: material.name,
        percentage: 0,
        grams: 0,
        notes: "",
        // Store note_type for categorization (top/heart/base)
        note_type: material.note_type || "unclassified",
        // Store odor_impact for power calculation
        odor_impact: material.odor_impact || 500,
        // Store solution info for calculation
        solution_percentage: material.solution_percentage || 100,
        solution_type: material.solution_type || "Alcohol",
        // Store price for cost calculation
        price_per_gram: material.price_per_gram || 0,
        // Store IFRA limit for validation
        ifra_limit: material.ifra_limit
      }]
    }));
    setSearchQuery("");
  };

  const updateIngredient = (index, field, value) => {
    const newIngredients = [...recipe.ingredients];
    newIngredients[index][field] = value;
    
    // Calculate raw material budget based on concentration
    const rawMaterialBudget = (recipe.concentration_percent / 100) * recipe.total_grams;

    // Auto-calculate grams when percentage changes
    // Percentage is % of the RAW MATERIAL BUDGET, not total batch
    if (field === "percentage") {
      const pct = parseFloat(value) || 0;
      // Raw material grams needed
      const rawGramsNeeded = (pct / 100) * rawMaterialBudget;
      // Account for dilution: if material is 50% solution, need 2x to get the raw amount
      const solutionPct = newIngredients[index].solution_percentage || 100;
      const dilutedGrams = solutionPct > 0 ? rawGramsNeeded / (solutionPct / 100) : rawGramsNeeded;
      newIngredients[index].grams = dilutedGrams;
    }
    // Auto-calculate percentage when grams changes
    if (field === "grams") {
      const grams = parseFloat(value) || 0;
      // Convert diluted grams to raw material grams
      const solutionPct = newIngredients[index].solution_percentage || 100;
      const rawGrams = grams * (solutionPct / 100);
      // Calculate what percentage of raw budget this represents
      const pct = rawMaterialBudget > 0 ? (rawGrams / rawMaterialBudget) * 100 : 0;
      newIngredients[index].percentage = pct;
    }

    setRecipe(prev => ({ ...prev, ingredients: newIngredients }));
  };

  const removeIngredient = (index) => {
    setRecipe(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  const useMaterials = async () => {
    if (!recipeId) {
      toast.error("Please save the recipe first");
      return;
    }

    setUsingMaterials(true);
    try {
      const response = await axios.post(
        `${API}/recipes/use-materials`,
        { recipe_id: recipeId, multiplier: batchMultiplier },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`Materials deducted for ${batchMultiplier}x batch!`);
        setShowUseDialog(false);
        // Refresh materials to show updated stock
        fetchMaterials();
      } else {
        response.data.errors.forEach(err => toast.error(err));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to use materials");
    } finally {
      setUsingMaterials(false);
    }
  };

  const recalculateGrams = () => {
    const rawMaterialBudget = (recipe.concentration_percent / 100) * recipe.total_grams;
    setRecipe(prev => ({
      ...prev,
      ingredients: prev.ingredients.map(ing => {
        const pct = parseFloat(ing.percentage) || 0;
        const rawGramsNeeded = (pct / 100) * rawMaterialBudget;
        const solutionPct = ing.solution_percentage || 100;
        const dilutedGrams = solutionPct > 0 ? rawGramsNeeded / (solutionPct / 100) : rawGramsNeeded;
        return {
          ...ing,
          grams: dilutedGrams
        };
      })
    }));
  };

  const totalPercentage = recipe.ingredients.reduce((sum, ing) => sum + (parseFloat(ing.percentage) || 0), 0);
  const totalGrams = recipe.ingredients.reduce((sum, ing) => sum + (parseFloat(ing.grams) || 0), 0);
  
  // Raw material budget based on concentration target
  const rawMaterialBudget = (parseFloat(recipe.concentration_percent) || 20) / 100 * recipe.total_grams;

  // Calculate actual raw material grams and solution breakdown
  const calculateRawAndSolution = () => {
    let totalRawMaterialGrams = 0;
    const solutionBreakdown = { Alcohol: 0, IPM: 0, DPG: 0, TEC: 0 };

    recipe.ingredients.forEach(ing => {
      const dilutedGrams = parseFloat(ing.grams) || 0;
      const solutionPct = parseFloat(ing.solution_percentage) || 100;
      const solutionType = ing.solution_type || "Alcohol";

      // Raw material grams = diluted grams * (solution_percentage / 100)
      // e.g., 2g of 50% solution = 1g raw material
      const rawGrams = dilutedGrams * (solutionPct / 100);
      const solutionGrams = dilutedGrams - rawGrams;

      totalRawMaterialGrams += rawGrams;
      if (solutionGrams > 0 && solutionBreakdown.hasOwnProperty(solutionType)) {
        solutionBreakdown[solutionType] += solutionGrams;
      }
    });

    return { totalRawMaterialGrams, solutionBreakdown };
  };

  const { totalRawMaterialGrams, solutionBreakdown } = calculateRawAndSolution();
  const actualRawPercent = recipe.total_grams > 0 ? (totalRawMaterialGrams / recipe.total_grams) * 100 : 0;
  const targetConcentration = parseFloat(recipe.concentration_percent) || 20;

  // IFRA Limit Validation Functions
  const parseIfraLimit = (ifraLimit) => {
    if (ifraLimit === null || ifraLimit === undefined) return null;
    if (ifraLimit === "No restriction" || ifraLimit === "no restriction") return null;
    
    // If it's already a number, return it directly
    if (typeof ifraLimit === 'number') {
      return ifraLimit;
    }
    
    // Convert to string for parsing
    const ifraStr = String(ifraLimit);
    
    // Handle percentage values (e.g., "2%", "2.5%", "50%")
    const percentMatch = ifraStr.match(/^(\d+\.?\d*)%?$/);
    if (percentMatch) {
      return parseFloat(percentMatch[1]);
    }
    
    // Handle specific format cases (e.g., "2.0%", "0.5%")
    const specificMatch = ifraStr.match(/(\d+\.?\d*)\s*%/);
    if (specificMatch) {
      return parseFloat(specificMatch[1]);
    }
    
    // Try to parse as a plain number
    const numValue = parseFloat(ifraStr);
    if (!isNaN(numValue)) {
      return numValue;
    }
    
    return null;
  };

  const isIFRALimitExceeded = (ingredient) => {
    const currentPercentage = parseFloat(ingredient.percentage) || 0;
    const ifraLimit = parseIfraLimit(ingredient.ifra_limit);
    
    return ifraLimit !== null && currentPercentage > ifraLimit;
  };

  const getIFRALimitDisplay = (ingredient) => {
    const ifraLimit = parseIfraLimit(ingredient.ifra_limit);
    return ifraLimit !== null ? `max ${ifraLimit}%` : null;
  };

  // Calculate total batch price based on ingredient costs
  const totalBatchPrice = recipe.ingredients.reduce((sum, ing) => {
    const grams = parseFloat(ing.grams) || 0;
    const pricePerGram = parseFloat(ing.price_per_gram) || 0;
    return sum + (grams * pricePerGram);
  }, 0);

  // Export recipe as JSON file
  const exportRecipe = () => {
    const exportData = {
      name: recipe.name,
      description: recipe.description,
      concentration_percent: recipe.concentration_percent,
      total_grams: recipe.total_grams,
      notes: recipe.notes,
      ingredients: recipe.ingredients.map(ing => ({
        material_name: ing.material_name,
        percentage: ing.percentage,
        grams: ing.grams,
        duration: ing.duration,
        solution_percentage: ing.solution_percentage,
        solution_type: ing.solution_type
      })),
      exported_at: new Date().toISOString(),
      app: "Scentic"
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${recipe.name || 'recipe'}_scentic.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Recipe exported!");
  };

  // AI Resolution for unclassified ingredients
  const resolveUnclassifiedWithAI = async () => {
    const unclassified = recipe.ingredients.filter(
      ing => !ing.note_type || ing.note_type === "unclassified"
    );
    
    if (unclassified.length === 0) {
      toast.info("No unclassified ingredients to resolve");
      return;
    }
    
    setResolvingAI(true);
    setShowAIPanel(true);
    
    try {
      const response = await axios.post(
        `${API}/ai/resolve-unclassified`,
        {
          ingredients: unclassified.map(ing => ({
            material_name: ing.material_name,
            percentage: ing.percentage,
            grams: ing.grams
          }))
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAiResolutions(response.data.resolutions);
      
      if (response.data.resolved_count > 0) {
        toast.success(`Found ${response.data.resolved_count} matches in your inventory!`);
      } else if (response.data.suggest_alternative_count > 0) {
        toast.info(`${response.data.suggest_alternative_count} alternatives suggested`);
      }
    } catch (error) {
      console.error("AI resolution error:", error);
      toast.error("Failed to resolve ingredients");
    } finally {
      setResolvingAI(false);
    }
  };

  // Apply AI suggestion - link ingredient to inventory material
  const applyAISuggestion = (resolution) => {
    if (!resolution.suggested_material) return;
    
    const material = materials.find(m => m.material_id === resolution.suggested_material.material_id);
    if (!material) {
      toast.error("Material not found in inventory");
      return;
    }
    
    // Find and update the ingredient
    const newIngredients = recipe.ingredients.map(ing => {
      if (ing.material_name === resolution.original_name) {
        return {
          ...ing,
          material_id: material.material_id,
          material_name: material.name,
          note_type: material.note_type || ing.note_type,
          odor_impact: material.odor_impact || 500,
          solution_percentage: material.solution_percentage || 100,
          solution_type: material.solution_type || "Alcohol",
          price_per_gram: material.price_per_gram || 0,
          ifra_limit: material.ifra_limit
        };
      }
      return ing;
    });
    
    setRecipe(prev => ({ ...prev, ingredients: newIngredients }));
    
    // Remove from resolutions
    setAiResolutions(prev => prev.filter(r => r.original_name !== resolution.original_name));
    
    const isAlternative = resolution.resolution_type === "suggest_alternative";
    toast.success(isAlternative 
      ? `Using "${material.name}" as alternative for "${resolution.original_name}"`
      : `Linked "${resolution.original_name}" to "${material.name}"`
    );
  };

  // Determine status color
  const getStatusColor = () => {
    const tolerance = 2; // Allow 2% tolerance
    if (actualRawPercent < targetConcentration - tolerance) return "blue"; // Under
    if (actualRawPercent > targetConcentration + tolerance) return "red"; // Over
    return "green"; // Within range
  };
  const statusColor = getStatusColor();

  // Group ingredients by note type (top, heart, base, unclassified)
  const categorizedIngredients = {
    top: recipe.ingredients.filter(ing => ing.note_type === "top"),
    heart: recipe.ingredients.filter(ing => ing.note_type === "heart"),
    base: recipe.ingredients.filter(ing => ing.note_type === "base"),
    unclassified: recipe.ingredients.filter(ing => !ing.note_type || ing.note_type === "unclassified")
  };

  // Calculate totals per category
  const categoryTotals = {
    top: categorizedIngredients.top.reduce((sum, ing) => sum + (parseFloat(ing.percentage) || 0), 0),
    heart: categorizedIngredients.heart.reduce((sum, ing) => sum + (parseFloat(ing.percentage) || 0), 0),
    base: categorizedIngredients.base.reduce((sum, ing) => sum + (parseFloat(ing.percentage) || 0), 0),
    unclassified: categorizedIngredients.unclassified.reduce((sum, ing) => sum + (parseFloat(ing.percentage) || 0), 0)
  };

  const filteredMaterials = materials.filter(m => {
    const query = searchQuery.toLowerCase();
    return (
      m.name.toLowerCase().includes(query) ||
      (m.synonyms && m.synonyms.toLowerCase().includes(query)) || // Search synonyms
      (m.cas_number && m.cas_number.toLowerCase().includes(query)) ||
      (m.description && m.description.toLowerCase().includes(query)) ||
      (m.odor_profile && m.odor_profile.toLowerCase().includes(query)) ||
      (m.category && m.category.toLowerCase().includes(query))
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-noir flex">
        <Sidebar />
        <main className="flex-1 p-8 ml-64 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-amber border-t-transparent rounded-full animate-spin"></div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-noir flex">
      <Sidebar />
      
      <main className="flex-1 p-8 ml-64">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-playfair text-3xl font-bold text-white mb-2">
                {recipeId ? "Edit Recipe" : "Recipe Builder"}
              </h1>
              <p className="text-zinc-400">Create and calculate your formula</p>
            </div>
            <div className="flex gap-3 items-center">
              {/* Export Button */}
              {recipe.ingredients.length > 0 && (
                <Button 
                  data-testid="export-recipe-btn"
                  onClick={exportRecipe}
                  variant="outline"
                  className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              )}
              
              {recipeId && recipe.ingredients.length > 0 && (
                <Button 
                  data-testid="use-materials-btn"
                  onClick={() => setShowUseDialog(true)}
                  variant="outline"
                  className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                >
                  <Package className="w-4 h-4 mr-2" />
                  Use Materials
                </Button>
              )}
              <Button 
                data-testid="save-recipe-btn"
                onClick={handleSave}
                disabled={saving}
                className="bg-amber hover:bg-amber-light text-black font-semibold uppercase tracking-widest text-xs"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save Recipe"}
              </Button>
            </div>
          </div>

          {/* Use Materials Dialog */}
          <Dialog open={showUseDialog} onOpenChange={setShowUseDialog}>
            <DialogContent className="bg-noir-paper border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-white font-playfair">Use Materials</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-zinc-400">
                  This will deduct materials from your inventory based on the recipe amounts.
                </p>
                
                <div className="space-y-2">
                  <Label className="text-zinc-300">How many batches?</Label>
                  <Input
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={batchMultiplier}
                    onChange={(e) => setBatchMultiplier(parseFloat(e.target.value) || 1)}
                    className="bg-noir-subtle border-zinc-700 text-white font-mono"
                  />
                  <p className="text-xs text-zinc-500">
                    1 batch = {recipe.total_grams}g total
                    {batchMultiplier !== 1 && ` • ${batchMultiplier} batches = ${(recipe.total_grams * batchMultiplier).toFixed(1)}g total`}
                  </p>
                </div>

                <div className="p-3 bg-zinc-800/50 border border-zinc-700 space-y-2">
                  <p className="text-sm text-zinc-300 font-medium">Materials to deduct:</p>
                  {recipe.ingredients.map((ing, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-zinc-400">{ing.material_name}</span>
                      <span className="text-amber font-mono">{((ing.grams || 0) * batchMultiplier).toFixed(3)}g</span>
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="ghost" className="text-zinc-400">Cancel</Button>
                </DialogClose>
                <Button
                  onClick={useMaterials}
                  disabled={usingMaterials}
                  className="bg-green-600 hover:bg-green-500 text-white"
                >
                  {usingMaterials ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Processing...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Confirm & Deduct
                    </span>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Panel - Recipe Details & Ingredients */}
            <div className="lg:col-span-2 space-y-6">
              {/* Recipe Info */}
              <Card className="bg-noir-paper border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white">Recipe Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Recipe Name *</Label>
                      <Input
                        data-testid="recipe-name-input"
                        value={recipe.name}
                        onChange={(e) => setRecipe(prev => ({ ...prev, name: e.target.value }))}
                        className="bg-noir-subtle border-zinc-700 text-white"
                        placeholder="e.g., Summer Breeze"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Target Concentration (%)</Label>
                      <Input
                        data-testid="recipe-concentration-input"
                        type="number"
                        min="1"
                        max="100"
                        value={recipe.concentration_percent}
                        onChange={(e) => setRecipe(prev => ({ ...prev, concentration_percent: parseFloat(e.target.value) || 20 }))}
                        className="bg-noir-subtle border-zinc-700 text-white font-mono"
                      />
                      <p className="text-xs text-zinc-500">% of raw materials (rest is solution)</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-zinc-300">Description</Label>
                    <textarea
                      data-testid="recipe-description-input"
                      value={recipe.description}
                      onChange={(e) => setRecipe(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full h-20 bg-noir-subtle border border-zinc-700 text-white p-3 resize-none focus:border-amber focus:outline-none"
                      placeholder="Describe your fragrance..."
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Total Batch (grams)</Label>
                      <Input
                        data-testid="recipe-total-grams-input"
                        type="number"
                        min="1"
                        value={recipe.total_grams}
                        onChange={(e) => {
                          setRecipe(prev => ({ ...prev, total_grams: parseFloat(e.target.value) || 100 }));
                        }}
                        onBlur={recalculateGrams}
                        className="bg-noir-subtle border-zinc-700 text-white font-mono"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Notes</Label>
                      <Input
                        value={recipe.notes || ""}
                        onChange={(e) => setRecipe(prev => ({ ...prev, notes: e.target.value }))}
                        className="bg-noir-subtle border-zinc-700 text-white"
                        placeholder="Additional notes..."
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Ingredients Table */}
              <Card className="bg-noir-paper border-zinc-800">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white">Ingredients</CardTitle>
                  <div className="flex items-center gap-3 text-sm">
                    <span className={`font-mono ${totalPercentage > 100 ? 'text-red-500' : 'text-amber'}`}>
                      {totalPercentage.toFixed(3)}%
                    </span>
                    <span className="text-zinc-500">|</span>
                    <span className="text-zinc-400 font-mono">{totalGrams.toFixed(3)}g</span>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Concentration Status Bar */}
                  {recipe.ingredients.length > 0 && (
                    <div className={`p-3 mb-4 border ${
                      statusColor === 'blue' ? 'bg-blue-500/10 border-blue-500/30' :
                      statusColor === 'green' ? 'bg-green-500/10 border-green-500/30' :
                      'bg-red-500/10 border-red-500/30'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-medium ${
                          statusColor === 'blue' ? 'text-blue-400' :
                          statusColor === 'green' ? 'text-green-400' :
                          'text-red-400'
                        }`}>
                          {statusColor === 'blue' ? 'Under Target' :
                           statusColor === 'green' ? 'On Target' :
                           'Over Target'}
                        </span>
                        <span className={`font-mono text-sm ${
                          statusColor === 'blue' ? 'text-blue-400' :
                          statusColor === 'green' ? 'text-green-400' :
                          'text-red-400'
                        }`}>
                          {actualRawPercent.toFixed(1)}% / {targetConcentration}% raw materials
                        </span>
                      </div>
                      <div className="h-2 bg-zinc-800 overflow-hidden">
                        <div 
                          className={`h-full transition-all ${
                            statusColor === 'blue' ? 'bg-blue-500' :
                            statusColor === 'green' ? 'bg-green-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${Math.min((actualRawPercent / targetConcentration) * 100, 150)}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Solution Breakdown - Shows solution to ADD */}
                  {recipe.ingredients.length > 0 && (
                    <div className="p-4 mb-4 bg-cyan-500/5 border border-cyan-500/30">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Droplet className="w-4 h-4 text-cyan-400" />
                          <span className="text-cyan-300 text-sm font-medium">Solution to Add</span>
                        </div>
                        <span className="text-cyan-400 font-mono text-lg">
                          {(recipe.total_grams - totalGrams).toFixed(3)}g
                        </span>
                      </div>
                      
                      {/* What's in ingredients already */}
                      <div className="mb-3 p-2 bg-zinc-800/50 border border-zinc-700">
                        <p className="text-zinc-400 text-xs mb-1">From diluted materials:</p>
                        <div className="grid grid-cols-4 gap-2 text-xs">
                          {Object.entries(solutionBreakdown).map(([type, grams]) => (
                            <div key={type} className="text-center">
                              <p className={grams > 0 ? 'text-zinc-300' : 'text-zinc-600'}>{type}</p>
                              <p className={`font-mono ${grams > 0 ? 'text-zinc-300' : 'text-zinc-700'}`}>{grams.toFixed(3)}g</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Summary */}
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div className="text-center p-2 bg-amber/10 border border-amber/30">
                          <p className="text-amber text-xs">Raw Materials</p>
                          <p className="text-amber font-mono font-medium">{totalRawMaterialGrams.toFixed(3)}g</p>
                        </div>
                        <div className="text-center p-2 bg-zinc-800/50 border border-zinc-700">
                          <p className="text-zinc-400 text-xs">In Dilutions</p>
                          <p className="text-zinc-300 font-mono font-medium">{(totalGrams - totalRawMaterialGrams).toFixed(3)}g</p>
                        </div>
                        <div className="text-center p-2 bg-cyan-500/10 border border-cyan-500/30">
                          <p className="text-cyan-400 text-xs">Add Solution</p>
                          <p className="text-cyan-300 font-mono font-medium">{Math.max(0, recipe.total_grams - totalGrams).toFixed(3)}g</p>
                        </div>
                      </div>
                      
                      <p className="text-xs text-zinc-500 mt-2">
                        Total batch: {recipe.total_grams}g = {totalRawMaterialGrams.toFixed(3)}g raw + {(recipe.total_grams - totalRawMaterialGrams).toFixed(3)}g solution
                      </p>
                    </div>
                  )}

                  {totalPercentage > 100 && (
                    <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 mb-4 text-red-400">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-sm">Total exceeds 100%</span>
                    </div>
                  )}

                  {recipe.ingredients.length === 0 ? (
                    <div className="text-center py-8">
                      <FlaskConical className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                      <p className="text-zinc-500">No ingredients yet</p>
                      <p className="text-zinc-600 text-sm">Search and add materials from the right panel</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Top Notes */}
                      {categorizedIngredients.top.length > 0 && (
                        <NoteSection 
                          title="Top Notes"
                          subtitle="First impression, evaporates quickly (15-30 min)"
                          color="text-blue-400"
                          bgColor="bg-blue-500/10"
                          borderColor="border-blue-500/30"
                          ingredients={categorizedIngredients.top}
                          totalPercentage={categoryTotals.top}
                          allIngredients={recipe.ingredients}
                          updateIngredient={updateIngredient}
                          removeIngredient={removeIngredient}
                          isIFRALimitExceeded={isIFRALimitExceeded}
                          getIFRALimitDisplay={getIFRALimitDisplay}
                        />
                      )}

                      {/* Heart Notes */}
                      {categorizedIngredients.heart.length > 0 && (
                        <NoteSection 
                          title="Heart Notes"
                          subtitle="The core of the fragrance (2-4 hours)"
                          color="text-purple-400"
                          bgColor="bg-purple-500/10"
                          borderColor="border-purple-500/30"
                          ingredients={categorizedIngredients.heart}
                          totalPercentage={categoryTotals.heart}
                          allIngredients={recipe.ingredients}
                          updateIngredient={updateIngredient}
                          removeIngredient={removeIngredient}
                          isIFRALimitExceeded={isIFRALimitExceeded}
                          getIFRALimitDisplay={getIFRALimitDisplay}
                        />
                      )}

                      {/* Base Notes */}
                      {categorizedIngredients.base.length > 0 && (
                        <NoteSection 
                          title="Base Notes"
                          subtitle="Foundation, longest lasting (4-24+ hours)"
                          color="text-amber"
                          bgColor="bg-amber/10"
                          borderColor="border-amber/30"
                          ingredients={categorizedIngredients.base}
                          totalPercentage={categoryTotals.base}
                          allIngredients={recipe.ingredients}
                          updateIngredient={updateIngredient}
                          removeIngredient={removeIngredient}
                          isIFRALimitExceeded={isIFRALimitExceeded}
                          getIFRALimitDisplay={getIFRALimitDisplay}
                        />
                      )}

                      {/* Unclassified Section with AI Assistant */}
                      {categorizedIngredients.unclassified.length > 0 && (
                        <div className="space-y-3">
                          {/* Unclassified Ingredients List */}
                          <NoteSection 
                            title="Unclassified"
                            subtitle="Materials not linked to inventory"
                            color="text-zinc-400"
                            bgColor="bg-zinc-500/10"
                            borderColor="border-zinc-500/30"
                            ingredients={categorizedIngredients.unclassified}
                            totalPercentage={categoryTotals.unclassified}
                            allIngredients={recipe.ingredients}
                            updateIngredient={updateIngredient}
                            removeIngredient={removeIngredient}
                            isIFRALimitExceeded={isIFRALimitExceeded}
                            getIFRALimitDisplay={getIFRALimitDisplay}
                          />
                          
                          {/* AI Resolution Button */}
                          <div className="flex justify-center">
                            <Button
                              data-testid="ai-resolve-btn"
                              onClick={resolveUnclassifiedWithAI}
                              disabled={resolvingAI}
                              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-medium gap-2"
                            >
                              {resolvingAI ? (
                                <>
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                  Analyzing...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-4 h-4" />
                                  AI: Find or Suggest Materials
                                </>
                              )}
                            </Button>
                          </div>
                          
                          {/* AI Resolution Results Panel */}
                          {showAIPanel && aiResolutions.length > 0 && (
                            <div className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 border border-violet-500/30 p-4 space-y-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Sparkles className="w-5 h-5 text-violet-400" />
                                  <h4 className="text-violet-300 font-medium">AI Suggestions</h4>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setShowAIPanel(false)}
                                  className="text-zinc-400 hover:text-white h-7 w-7 p-0"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                              
                              <div className="space-y-3">
                                {aiResolutions.map((resolution, idx) => (
                                  <AIResolutionCard
                                    key={idx}
                                    resolution={resolution}
                                    onApply={() => applyAISuggestion(resolution)}
                                    materials={materials}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Summary Bar */}
                      <div className="pt-4 border-t border-zinc-800 space-y-3">
                        <div className="flex items-center gap-4 text-xs">
                          <span className="text-zinc-500">Composition:</span>
                          {categoryTotals.top > 0 && (
                            <span className="text-blue-400">Top {categoryTotals.top.toFixed(1)}%</span>
                          )}
                          {categoryTotals.heart > 0 && (
                            <span className="text-purple-400">Heart {categoryTotals.heart.toFixed(1)}%</span>
                          )}
                          {categoryTotals.base > 0 && (
                            <span className="text-amber">Base {categoryTotals.base.toFixed(1)}%</span>
                          )}
                          {categoryTotals.unclassified > 0 && (
                            <span className="text-zinc-400">Other {categoryTotals.unclassified.toFixed(1)}%</span>
                          )}
                        </div>
                        
                        {/* Batch Price */}
                        {totalBatchPrice > 0 && (
                          <div className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/30">
                            <span className="text-green-400 text-sm font-medium flex items-center gap-2">
                              <DollarSign className="w-4 h-4" />
                              Batch Cost
                            </span>
                            <span className="text-green-300 font-mono text-lg">${totalBatchPrice.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Ternary Diagram - Composition Visualization */}
              {recipe.ingredients.length > 0 && (
                <TernaryDiagram 
                  ingredients={recipe.ingredients}
                  materials={materials}
                />
              )}

              {/* Notes */}
              <Card className="bg-noir-paper border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white">Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <textarea
                    value={recipe.notes}
                    onChange={(e) => setRecipe(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full h-32 bg-noir-subtle border border-zinc-700 text-white p-3 resize-none focus:border-amber focus:outline-none"
                    placeholder="Add any notes about this recipe..."
                  />
                </CardContent>
              </Card>
            </div>

            {/* Right Panel - Material Search */}
            <div className="space-y-6">
              <Card className="bg-noir-paper border-zinc-800 sticky top-8">
                <CardHeader>
                  <CardTitle className="text-white">Add Materials</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      data-testid="material-search-recipe"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search materials..."
                      className="pl-10 bg-noir-subtle border-zinc-700 text-white"
                    />
                  </div>

                  <div className="max-h-[400px] overflow-y-auto space-y-2">
                    {materials.length === 0 ? (
                      <div className="text-center py-6">
                        <p className="text-zinc-500 text-sm">No materials in inventory</p>
                        <Button
                          variant="link"
                          className="text-amber"
                          onClick={() => navigate("/inventory")}
                        >
                          Add materials first
                        </Button>
                      </div>
                    ) : filteredMaterials.length === 0 ? (
                      <p className="text-zinc-500 text-sm text-center py-4">No materials found</p>
                    ) : (
                      filteredMaterials.map((material) => {
                        const isAdded = recipe.ingredients.some(i => i.material_id === material.material_id);
                        const getDurationColor = (dur) => {
                          if (dur === "top") return "text-blue-400";
                          if (dur === "heart") return "text-purple-400";
                          if (dur === "base") return "text-amber";
                          return "text-zinc-500";
                        };
                        return (
                          <button
                            key={material.material_id}
                            onClick={() => addIngredient(material)}
                            disabled={isAdded}
                            className={`w-full text-left p-3 border transition-colors ${
                              isAdded 
                                ? "bg-amber/10 border-amber/30 cursor-not-allowed" 
                                : "bg-noir-subtle border-zinc-800 hover:border-amber/30"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="min-w-0 flex-1">
                                <p className="text-white font-medium">{material.name}</p>
                                {material.synonyms && (
                                  <p className="text-blue-400 text-xs truncate" title={material.synonyms}>
                                    {material.synonyms}
                                  </p>
                                )}
                                <div className="flex items-center gap-2 text-xs">
                                  <span className="text-zinc-500 font-mono">
                                    {material.cas_number || "No CAS"}
                                  </span>
                                  {material.note_type && (
                                    <span className={`${getDurationColor(material.note_type)}`}>
                                      • {material.note_type}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {isAdded ? (
                                <span className="text-amber text-xs">Added</span>
                              ) : (
                                <Plus className="w-4 h-4 text-zinc-500" />
                              )}
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

// Note Section Component for categorized ingredients
const NoteSection = ({ 
  title, 
  subtitle, 
  color, 
  bgColor, 
  borderColor, 
  ingredients, 
  totalPercentage,
  allIngredients,
  updateIngredient,
  removeIngredient,
  isIFRALimitExceeded,
  getIFRALimitDisplay
}) => {
  return (
    <div className={`${bgColor} border ${borderColor} p-4`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className={`font-medium ${color}`}>{title}</h4>
          <p className="text-zinc-500 text-xs">{subtitle}</p>
        </div>
        <span className={`font-mono text-sm ${color}`}>{totalPercentage.toFixed(3)}%</span>
      </div>
      
      <div className="space-y-2">
        {ingredients.map((ingredient) => {
          // Find the actual index in the full ingredients array
          const actualIndex = allIngredients.findIndex(
            ing => ing.material_id === ingredient.material_id
          );
          
          return (
            <div 
              key={ingredient.material_id}
              className="flex items-center gap-3 bg-noir-paper/50 p-2 border border-zinc-800/50"
            >
              <div className="flex-1">
                <span className="text-white text-sm truncate">
                  {ingredient.material_name}
                  {isIFRALimitExceeded(ingredient) && (
                    <span className="ml-2 text-red-400 font-medium text-xs">
                      ({getIFRALimitDisplay(ingredient)})
                    </span>
                  )}
                </span>
              </div>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={ingredient.percentage || ""}
                onChange={(e) => updateIngredient(actualIndex, "percentage", parseFloat(e.target.value) || 0)}
                className={`font-mono text-right h-7 w-16 text-sm ${
                  isIFRALimitExceeded(ingredient) 
                    ? "bg-red-900/50 border-red-500 text-red-100" 
                    : "bg-noir-subtle border-zinc-700 text-white"
                }`}
              />
              <span className="text-zinc-500 text-xs w-4">%</span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={ingredient.grams ? ingredient.grams.toFixed(3) : ""}
                onChange={(e) => updateIngredient(actualIndex, "grams", parseFloat(e.target.value) || 0)}
                className="bg-noir-subtle border-zinc-700 text-white font-mono text-right h-7 w-16 text-sm"
              />
              <span className="text-zinc-500 text-xs w-4">g</span>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => removeIngredient(actualIndex)}
                className="h-7 w-7 text-zinc-500 hover:text-red-400"
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// AI Resolution Card Component
const AIResolutionCard = ({ resolution, onApply, materials }) => {
  const isMatch = resolution.resolution_type === "inventory_match" || 
                  resolution.resolution_type === "ai_inventory_match";
  const isAlternative = resolution.resolution_type === "suggest_alternative";
  const isNoMatch = resolution.resolution_type === "no_match";
  const confidence = resolution.confidence || 0;
  
  const getConfidenceColor = () => {
    if (confidence >= 85) return "text-green-400";
    if (confidence >= 60) return "text-yellow-400";
    if (confidence > 0) return "text-orange-400";
    return "text-zinc-400";
  };
  
  const getConfidenceBg = () => {
    if (confidence >= 85) return "bg-green-500/20";
    if (confidence >= 60) return "bg-yellow-500/20";
    if (confidence > 0) return "bg-orange-500/20";
    return "bg-zinc-500/20";
  };
  
  return (
    <div className="bg-noir-paper/70 border border-zinc-700 p-3 space-y-2">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <p className="text-white font-medium text-sm">
            &ldquo;{resolution.original_name}&rdquo;
          </p>
          {resolution.percentage > 0 && (
            <p className="text-zinc-500 text-xs">
              {resolution.percentage}% • {resolution.grams?.toFixed(3)}g
            </p>
          )}
        </div>
        {confidence > 0 && (
          <span className={`text-xs px-2 py-0.5 rounded ${getConfidenceBg()} ${getConfidenceColor()}`}>
            {confidence}% match
          </span>
        )}
        {isAlternative && confidence === 0 && (
          <span className="text-xs px-2 py-0.5 rounded bg-amber/20 text-amber">
            Alternative
          </span>
        )}
      </div>
      
      {/* AI Reasoning */}
      <p className="text-zinc-400 text-xs italic">
        {resolution.reasoning}
      </p>
      
      {/* Match Found - Show Link Button (Green) */}
      {isMatch && resolution.suggested_material && (
        <div className="flex items-center justify-between gap-2 p-2 bg-green-500/10 border border-green-500/30">
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-green-400" />
            <div>
              <p className="text-green-300 text-sm font-medium">
                {resolution.suggested_material.name}
              </p>
              {resolution.suggested_material.note_type && (
                <p className="text-green-400/70 text-xs">
                  {resolution.suggested_material.note_type} note
                </p>
              )}
            </div>
          </div>
          <Button
            size="sm"
            data-testid={`apply-suggestion-${resolution.original_name}`}
            onClick={onApply}
            className="bg-green-600 hover:bg-green-500 text-white h-7 text-xs"
          >
            <ArrowRight className="w-3 h-3 mr-1" />
            Link
          </Button>
        </div>
      )}
      
      {/* Alternative Suggestion - Show Use Alternative Button (Amber) */}
      {isAlternative && resolution.suggested_material && (
        <div className="flex items-center justify-between gap-2 p-2 bg-amber/10 border border-amber/30">
          <div className="flex items-center gap-2">
            <HelpCircle className="w-4 h-4 text-amber" />
            <div>
              <p className="text-amber text-sm font-medium">
                Suggested: {resolution.suggested_material.name}
              </p>
              {resolution.alternative_reason && (
                <p className="text-amber/70 text-xs">
                  {resolution.alternative_reason}
                </p>
              )}
            </div>
          </div>
          <Button
            size="sm"
            data-testid={`use-alternative-${resolution.original_name}`}
            onClick={onApply}
            className="bg-amber hover:bg-amber/80 text-black h-7 text-xs"
          >
            <ArrowRight className="w-3 h-3 mr-1" />
            Use
          </Button>
        </div>
      )}
      
      {/* No Match Found */}
      {isNoMatch && (
        <div className="flex items-center gap-2 p-2 bg-red-500/10 border border-red-500/30">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <p className="text-red-300 text-sm">
            No match or alternative found in your inventory
          </p>
        </div>
      )}
      
      {/* Alternative Matches */}
      {resolution.alternatives && resolution.alternatives.length > 0 && (
        <div className="pt-2 border-t border-zinc-700/50">
          <p className="text-zinc-500 text-xs mb-1">Other possibilities:</p>
          <div className="flex flex-wrap gap-1">
            {resolution.alternatives.map((alt, i) => (
              <span 
                key={i} 
                className="text-xs px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded"
              >
                {alt}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
